import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
const parseHashValue = (hashValue) => {
    const sanitizedHash = hashValue.replace(/^#/, "");
    if (!sanitizedHash) {
        return { sectionId: "" };
    }
    const [sectionId] = sanitizedHash.split("&").filter(Boolean);
    return { sectionId };
};
const getScrollOffset = (container, element, offset = 0) => element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - offset;
/**
 * Scroll-spy for an in-page section nav: keeps one section active as the user scrolls, jumps
 * to a section on demand, and syncs the active section with the URL hash in both directions.
 *
 * Extracted verbatim from the Overview page (DISCOVER-964) so every consumer shares one copy.
 */
export const useSectionNav = ({ sections, containerRef, offset = 100, threshold = 120, canScrollToHash = true, }) => {
    const location = useLocation();
    const sectionRefs = useRef({});
    const isProgrammaticScrollRef = useRef(false);
    const scrollTimeoutRef = useRef(null);
    const isHashSyncRef = useRef(false);
    const activeSectionSourceRef = useRef(null);
    const [activeSection, setActiveSection] = useState("");
    // Recording the source is what stops a programmatic scroll from fighting the scroll
    // listener, and a hash-driven change from writing the hash straight back.
    const setActiveSectionWithSource = useCallback((sectionId, source) => {
        activeSectionSourceRef.current = source;
        setActiveSection(sectionId);
    }, []);
    const { sectionId: hashSectionId } = useMemo(() => parseHashValue(location.hash), [location.hash]);
    const registerSection = useCallback((sectionId) => (element) => {
        sectionRefs.current[sectionId] = element;
    }, []);
    const scrollToSection = useCallback((sectionId, behavior = "smooth") => {
        const container = containerRef.current;
        const element = sectionRefs.current[sectionId];
        // Mark it active up front. The scroll listener is muted for the next 500ms so it
        // cannot fight the programmatic scroll, which means it would otherwise be the user's
        // next manual scroll that finally updates the nav and the hash.
        setActiveSectionWithSource(sectionId, "nav");
        isProgrammaticScrollRef.current = true;
        if (element && container) {
            container.scrollTo({ top: getScrollOffset(container, element, offset), behavior });
        }
        else {
            container === null || container === void 0 ? void 0 : container.scrollTo({ top: 0, behavior });
        }
        if (scrollTimeoutRef.current)
            clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
            isProgrammaticScrollRef.current = false;
        }, 500);
    }, [containerRef, offset, setActiveSectionWithSource]);
    useEffect(() => {
        if (sections.length && !sections.find((section) => section.id === activeSection)) {
            const hasValidHash = Boolean(hashSectionId && sections.find((section) => section.id === hashSectionId));
            if (!hasValidHash) {
                setActiveSectionWithSource(sections[0].id, "init");
            }
        }
        else if (!sections.length && activeSection) {
            setActiveSectionWithSource("", "init");
        }
    }, [activeSection, hashSectionId, sections, setActiveSectionWithSource]);
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const resolveActiveSection = () => {
            var _a, _b;
            if (isProgrammaticScrollRef.current)
                return;
            if (!sections.length)
                return;
            const scrollTop = container.scrollTop;
            if (scrollTop < threshold) {
                const fallbackSection = (_b = (_a = sections[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "";
                if (fallbackSection && activeSection !== fallbackSection) {
                    setActiveSectionWithSource(fallbackSection, "scroll");
                }
                return;
            }
            for (let i = sections.length - 1; i >= 0; i -= 1) {
                const section = sections[i];
                const element = sectionRefs.current[section.id];
                if (element && scrollTop >= element.offsetTop - threshold) {
                    if (activeSection !== section.id) {
                        setActiveSectionWithSource(section.id, "scroll");
                    }
                    return;
                }
            }
        };
        // No debouncing: the highlight and the URL follow the user as they cross each section
        // header. That is cheap because the URL write below is not a navigation.
        container.addEventListener("scroll", resolveActiveSection);
        resolveActiveSection();
        return () => {
            container.removeEventListener("scroll", resolveActiveSection);
        };
    }, [activeSection, containerRef, sections, threshold, setActiveSectionWithSource]);
    // The mute belongs to the jump, not to this subscription: clearing it here killed the timer
    // that lifts it, and the listener stayed muted for the rest of the page's life.
    useEffect(() => () => {
        if (scrollTimeoutRef.current)
            clearTimeout(scrollTimeoutRef.current);
    }, []);
    // Active section -> hash.
    useEffect(() => {
        if (!activeSection)
            return;
        const source = activeSectionSourceRef.current;
        activeSectionSourceRef.current = null;
        if (source === "hash")
            return;
        const newHash = `#${activeSection}`;
        if (window.location.hash === newHash)
            return;
        // replaceState, not the router: a navigation makes the browser jump to whichever element
        // carries the new hash as its id, dragging the user to the top of a section they are only
        // scrolling past. This writes the address bar and nothing else.
        isHashSyncRef.current = true;
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
    }, [activeSection]);
    // Hash -> active section, and the deep-link jump.
    useEffect(() => {
        if (!hashSectionId)
            return;
        if (isHashSyncRef.current) {
            isHashSyncRef.current = false;
            return;
        }
        if (!sections.find((section) => section.id === hashSectionId))
            return;
        if (hashSectionId !== activeSection) {
            setActiveSectionWithSource(hashSectionId, "hash");
        }
        if (!canScrollToHash)
            return;
        if (!containerRef.current || !sectionRefs.current[hashSectionId])
            return;
        // "auto", not "smooth": a link a colleague sent must land where it points instead of
        // animating down from the top.
        scrollToSection(hashSectionId, "auto");
    }, [
        activeSection,
        canScrollToHash,
        containerRef,
        hashSectionId,
        scrollToSection,
        sections,
        setActiveSectionWithSource,
    ]);
    return { activeSectionId: activeSection, registerSection, scrollToSection };
};
